import { BuyAndGetConditionsTypes, DiscountCodeTypes } from "../../enum";
import { resolveBillableQuantity } from "./billableQuantity";
import { applyManualUnitPrice, resolvePercentageDiscountUnitPrice, resolveOrderLineCharge, getBuyXGetYFulfillmentCount, getBuyXGetYQuantityForCondition, isNoChargeReturnLine, matchesBuyXGetYCondition, normalizeBuyXGetYSelectedOptionId, normalizeBuyXGetYVariantScope, resolveBuyXGetYRewardSnapshot, } from "@washday/utilities";
const getNormalizedId = (value) => {
    if (value === null || value === undefined) {
        return "";
    }
    return String(value).trim();
};
const getProductIdentity = (product) => {
    var _a;
    const directId = getNormalizedId((_a = product === null || product === void 0 ? void 0 : product._id) !== null && _a !== void 0 ? _a : product === null || product === void 0 ? void 0 : product.productId);
    if (directId) {
        return directId;
    }
    const storeProductId = product === null || product === void 0 ? void 0 : product.storeProductId;
    if (typeof storeProductId === "string") {
        return getNormalizedId(storeProductId);
    }
    return getNormalizedId(storeProductId === null || storeProductId === void 0 ? void 0 : storeProductId._id);
};
const getDiscountTargetIdentity = (target) => {
    var _a, _b;
    if (typeof target === "string") {
        return getNormalizedId(target);
    }
    return getNormalizedId((_b = (_a = target === null || target === void 0 ? void 0 : target._id) !== null && _a !== void 0 ? _a : target === null || target === void 0 ? void 0 : target.productId) !== null && _b !== void 0 ? _b : target === null || target === void 0 ? void 0 : target.storeProductId);
};
const matchesDiscountTarget = (product, target) => {
    const productId = getProductIdentity(product);
    const targetId = getDiscountTargetIdentity(target);
    return Boolean(productId && targetId && productId === targetId);
};
export const normalizeSelectedOptionId = normalizeBuyXGetYSelectedOptionId;
export const normalizeVariantScope = normalizeBuyXGetYVariantScope;
export const matchesBuyCondition = matchesBuyXGetYCondition;
export const getQtyForBuyCondition = getBuyXGetYQuantityForCondition;
export const getFulfillmentCount = getBuyXGetYFulfillmentCount;
export const resolveGetConditionProductSnapshot = resolveBuyXGetYRewardSnapshot;
const getTargetProductIdSet = (products = []) => {
    return new Set((Array.isArray(products) ? products : [])
        .map((item) => getDiscountTargetIdentity(item))
        .filter((id) => id.length > 0));
};
export const getProductTaxesPercentage = (productObj, store) => {
    const getTaxValue = (tax, isTaxExempt) => {
        if (!tax) {
            // If no store tax configured, always return 0
            return 0;
        }
        if (isTaxExempt) {
            // If store tax is configured and taxExempt is true, return 0
            return 0;
        }
        // If store tax configured and taxExempt is false, return store tax
        return tax.value;
    };
    const tax1 = getTaxValue(store.taxOne, productObj.taxExemptOne);
    const tax2 = getTaxValue(store.taxTwo, productObj.taxExemptTwo);
    const tax3 = getTaxValue(store.taxThree, productObj.taxExemptThree);
    return tax1 + tax2 + tax3;
};
const TAX_SLOTS = [
    { key: "taxOne", taxField: "taxOne", exemptField: "taxExemptOne" },
    { key: "taxTwo", taxField: "taxTwo", exemptField: "taxExemptTwo" },
    { key: "taxThree", taxField: "taxThree", exemptField: "taxExemptThree" },
];
const round2 = (value) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
const getApplicableTaxesForProduct = (productObj, store) => {
    return TAX_SLOTS.reduce((acc, slot) => {
        var _a, _b;
        if (productObj[slot.exemptField]) {
            return acc;
        }
        const tax = store[slot.taxField];
        const rate = Number((_a = tax === null || tax === void 0 ? void 0 : tax.value) !== null && _a !== void 0 ? _a : 0);
        if (!Number.isFinite(rate) || rate <= 0) {
            return acc;
        }
        acc.push({
            key: slot.key,
            name: String((_b = tax === null || tax === void 0 ? void 0 : tax.name) !== null && _b !== void 0 ? _b : slot.key),
            rate,
            weight: Math.round(rate * 100),
        });
        return acc;
    }, []);
};
export const allocateByWeights = (total, weights) => {
    if (weights.length === 0)
        return [];
    if (total <= 0)
        return new Array(weights.length).fill(0);
    const safeWeights = weights.map((weight) => Math.max(0, Math.trunc(weight)));
    const sum = safeWeights.reduce((acc, weight) => acc + weight, 0);
    if (sum <= 0)
        return new Array(weights.length).fill(0);
    const raw = safeWeights.map((weight) => (total * weight) / sum);
    const floors = raw.map((amount) => Math.floor(amount));
    let remaining = total - floors.reduce((acc, amount) => acc + amount, 0);
    const order = raw
        .map((amount, index) => ({ index, frac: amount - Math.floor(amount) }))
        .sort((a, b) => b.frac - a.frac);
    const out = floors.slice();
    for (let index = 0; index < order.length && remaining > 0; index += 1) {
        out[order[index].index] += 1;
        remaining -= 1;
    }
    return out;
};
// Allocate a fixed order-level discount across eligible line bases (in cents).
// Each entry is capped at its corresponding base. Overflow caused by the cap
// is rerouted to the next-largest base. The result sums to
// min(totalCents, sum(baseCents)).
export const allocateFixedDiscountAcrossLines = (totalCents, baseCents) => {
    const len = baseCents.length;
    if (len === 0)
        return [];
    const safeBases = baseCents.map((b) => Math.max(0, Math.trunc(b)));
    const sumBases = safeBases.reduce((a, b) => a + b, 0);
    if (sumBases <= 0 || totalCents <= 0) {
        return new Array(len).fill(0);
    }
    const cappedTotal = Math.min(Math.trunc(totalCents), sumBases);
    const slices = allocateByWeights(cappedTotal, safeBases);
    let overflow = 0;
    for (let i = 0; i < len; i += 1) {
        if (slices[i] > safeBases[i]) {
            overflow += slices[i] - safeBases[i];
            slices[i] = safeBases[i];
        }
    }
    if (overflow === 0)
        return slices;
    const headroomOrder = safeBases
        .map((b, i) => ({ i, headroom: b - slices[i] }))
        .filter((entry) => entry.headroom > 0)
        .sort((a, b) => b.headroom - a.headroom);
    for (const entry of headroomOrder) {
        if (overflow <= 0)
            break;
        const take = Math.min(entry.headroom, overflow);
        slices[entry.i] += take;
        overflow -= take;
    }
    return slices;
};
export const aggregateTaxBreakdown = (items) => {
    const byKey = new Map();
    items.forEach((item) => {
        if (!item || Number(item.amount || 0) <= 0) {
            return;
        }
        const existing = byKey.get(item.key);
        if (existing) {
            existing.amount = round2(existing.amount + item.amount);
            return;
        }
        byKey.set(item.key, Object.assign(Object.assign({}, item), { amount: round2(item.amount) }));
    });
    return TAX_SLOTS.map((slot) => byKey.get(slot.key)).filter((item) => Boolean(item && item.amount > 0));
};
export const normalizeTaxBreakdownToTotal = (items, total) => {
    const target = round2(total);
    if (target <= 0) {
        return [];
    }
    const aggregated = aggregateTaxBreakdown(items);
    if (aggregated.length === 0) {
        return [];
    }
    const sum = round2(aggregated.reduce((acc, item) => acc + item.amount, 0));
    const diff = round2(target - sum);
    if (diff !== 0) {
        const lastIndex = aggregated.length - 1;
        aggregated[lastIndex] = Object.assign(Object.assign({}, aggregated[lastIndex]), { amount: round2(aggregated[lastIndex].amount + diff) });
    }
    return aggregated.filter((item) => item.amount > 0);
};
export const calculateProductTaxBreakdown = ({ product, store, lineTaxAmount, }) => {
    const applicableTaxes = getApplicableTaxesForProduct(product, store);
    const target = round2(lineTaxAmount);
    if (target <= 0 || applicableTaxes.length === 0) {
        return [];
    }
    const amounts = allocateByWeights(Math.round(target * 100), applicableTaxes.map((tax) => tax.weight)).map((amountCents) => round2(amountCents / 100));
    return normalizeTaxBreakdownToTotal(applicableTaxes.map((tax, index) => {
        var _a;
        return ({
            key: tax.key,
            name: tax.name,
            rate: tax.rate,
            amount: (_a = amounts[index]) !== null && _a !== void 0 ? _a : 0,
        });
    }), target);
};
export const getProductLineTotals = (productLinesTotals) => {
    const totalQuantity = +productLinesTotals.reduce((acum, line) => acum + line.qty, 0).toFixed(2);
    const totalImportWithDiscount = +productLinesTotals
        .reduce((acum, line) => acum + line.productLineTotalWithDiscount, 0)
        .toFixed(2);
    const totalImportWithoutDiscount = +productLinesTotals
        .reduce((acum, line) => acum + line.productLineImportTotal, 0)
        .toFixed(2);
    const totalImporTaxes = +productLinesTotals
        .reduce((acum, line) => acum + line.productLineTaxesTotal, 0)
        .toFixed(2);
    const totalDiscountAmount = +productLinesTotals
        .reduce((acum, line) => acum + line.lineDiscountAmount, 0)
        .toFixed(2);
    const totalSubtotalAmount = +productLinesTotals
        .reduce((acum, line) => acum + line.productLineSubtotal, 0)
        .toFixed(2);
    const taxBreakdown = normalizeTaxBreakdownToTotal(productLinesTotals.flatMap((line) => line.taxBreakdown || []), totalImporTaxes);
    // lineNetAfterDiscount is set by the included calculator (for over_price, productLineSubtotal IS net-after-discount)
    // lineGrossBeforeDiscount is set by the over_price calculator (for included, productLineImportTotal IS gross-before-discount)
    const totalNetAfterDiscount = +productLinesTotals
        .reduce((acum, line) => { var _a; return acum + ((_a = line.lineNetAfterDiscount) !== null && _a !== void 0 ? _a : line.productLineSubtotal); }, 0)
        .toFixed(2);
    const totalGrossBeforeDiscount = +productLinesTotals
        .reduce((acum, line) => { var _a; return acum + ((_a = line.lineGrossBeforeDiscount) !== null && _a !== void 0 ? _a : line.productLineImportTotal); }, 0)
        .toFixed(2);
    return {
        totalQuantity,
        totalImportWithDiscount,
        totalImportWithoutDiscount,
        totalImporTaxes,
        totalDiscountAmount,
        totalSubtotalAmount,
        taxBreakdown,
        totalNetAfterDiscount,
        totalGrossBeforeDiscount,
    };
};
export const getCreditApplied = (selectedCustomer, orderTotal) => {
    var _a, _b;
    const customerCredit = (_b = (_a = selectedCustomer === null || selectedCustomer === void 0 ? void 0 : selectedCustomer.customer) === null || _a === void 0 ? void 0 : _a.credit) !== null && _b !== void 0 ? _b : 0;
    if (customerCredit === 0 || !selectedCustomer) {
        return 0;
    }
    return Math.min(customerCredit, orderTotal);
};
export const productsMeetMinimumSubtotal = (productsArr, isExpress, minimumOrderSubtotal, returnPricing = {}) => {
    if (!Number.isFinite(minimumOrderSubtotal) || minimumOrderSubtotal <= 0)
        return true;
    const subtotal = (productsArr || []).reduce((sum, prod) => {
        var _a, _b, _c;
        if (!prod || prod.isBuyAndGetProduct || prod.isFreeItem)
            return sum;
        if (isNoChargeReturnLine(returnPricing, prod))
            return sum;
        if (prod.manualUnitPrice != null)
            return sum + resolveOrderLineCharge(Object.assign(Object.assign({}, prod), { discountAmount: 0 }), isExpress).subtotalBeforeDiscount;
        const price = Number((_b = (_a = (isExpress ? prod.expressPrice : prod.price)) !== null && _a !== void 0 ? _a : prod.price) !== null && _b !== void 0 ? _b : 0);
        const billableQuantity = resolveBillableQuantity(prod);
        const extra = Number((_c = prod.extraAmount) !== null && _c !== void 0 ? _c : 0);
        if (!Number.isFinite(price) || !Number.isFinite(billableQuantity))
            return sum;
        return sum + price * billableQuantity + (Number.isFinite(extra) ? extra : 0);
    }, 0);
    return subtotal >= minimumOrderSubtotal;
};
export const applyDiscountToProducts = (discountCode, productsArr, isExpress, storeDiscounts = [], selectedCustomer = { discount: 0 }, returnPricing = {}) => {
    var _a, _b, _c;
    productsArr = productsArr.map((line) => applyManualUnitPrice(line, isExpress));
    const couponIsIneligible = Boolean(discountCode &&
        !productsMeetMinimumSubtotal(productsArr, isExpress, Number((_a = discountCode.minimumOrderSubtotal) !== null && _a !== void 0 ? _a : 0), returnPricing));
    if (couponIsIneligible) {
        // An attached-but-ineligible coupon is still authoritative. Do not treat it
        // as "no coupon" and accidentally activate customer/store fallback discounts.
        return {
            newOrderProds: productsArr.map((prod) => (Object.assign(Object.assign({}, prod), { discountAmount: 0, discountAmountMode: 'per_unit' }))),
            buyAndGetProds: [],
        };
    }
    if (!discountCode) {
        return {
            newOrderProds: productsArr.map((prod) => {
                if (isNoChargeReturnLine(returnPricing, prod)) {
                    return Object.assign(Object.assign({}, prod), { discountAmount: 0, discountAmountMode: 'per_unit' });
                }
                const discountsToApply = storeDiscounts.filter((discount) => discount.isActive && discount.products.some((p) => {
                    return matchesDiscountTarget(prod, p);
                }));
                const customerDiscount = (selectedCustomer === null || selectedCustomer === void 0 ? void 0 : selectedCustomer.discount) / 100 || 0;
                const productPercentageDiscount = discountsToApply.reduce((prev, next) => {
                    if (next.type === 'percentage') {
                        return prev + next.value / 100;
                    }
                    return prev;
                }, 0);
                const prodPrice = resolvePercentageDiscountUnitPrice(prod, isExpress);
                const discountAmount = +((productPercentageDiscount + customerDiscount) * prodPrice).toFixed(2);
                return Object.assign(Object.assign({}, prod), { discountAmount, discountAmountMode: 'per_unit' });
            }),
            buyAndGetProds: []
        };
    }
    // Reiniciar los descuentos en los productos
    let newOrderProds = productsArr.map((prod) => (Object.assign(Object.assign({}, prod), { discountAmount: 0, discountAmountMode: 'per_unit' })));
    // Array para productos de descuento BUY_X_GET_Y o FREE_ITEM
    const buyAndGetProds = [];
    if (discountCode && discountCode.type === DiscountCodeTypes.PERCENTAGE) {
        const percentageDiscount = discountCode.value / 100;
        if (discountCode.applyToAllProducts) {
            newOrderProds = newOrderProds.map((prod) => {
                if (isNoChargeReturnLine(returnPricing, prod))
                    return prod;
                const prodPrice = resolvePercentageDiscountUnitPrice(prod, isExpress);
                const discountAmount = +(percentageDiscount * prodPrice).toFixed(2);
                return Object.assign(Object.assign({}, prod), { discountAmount: discountAmount });
            });
        }
        else {
            const discountProductIds = getTargetProductIdSet(discountCode.products);
            newOrderProds = newOrderProds.map((prod) => {
                if (isNoChargeReturnLine(returnPricing, prod))
                    return prod;
                if (!discountProductIds.has(getProductIdentity(prod))) {
                    return prod;
                }
                const prodPrice = resolvePercentageDiscountUnitPrice(prod, isExpress);
                const discountAmount = +(percentageDiscount * prodPrice).toFixed(2);
                return Object.assign(Object.assign({}, prod), { discountAmount });
            });
        }
    }
    if (discountCode && discountCode.type === DiscountCodeTypes.NUMBER) {
        const couponValue = Number(discountCode.value) || 0;
        const eligibilityIds = discountCode.applyToAllProducts
            ? null
            : getTargetProductIdSet(discountCode.products);
        const isEligibleProd = (prod) => !isNoChargeReturnLine(returnPricing, prod) &&
            (eligibilityIds === null || eligibilityIds.has(getProductIdentity(prod)));
        if (discountCode.applyOnceOnOrder) {
            // applyOnceOnOrder=true: allocate the fixed amount proportionally
            // across eligible lines so each line carries its share as a pre-tax
            // discountAmount. The downstream tax calc reads this and reduces the
            // taxable base before computing tax.
            const eligibleEntries = [];
            newOrderProds.forEach((prod, idx) => {
                var _a;
                if (!isEligibleProd(prod))
                    return;
                const prodPrice = isExpress
                    ? (_a = prod.expressPrice) !== null && _a !== void 0 ? _a : prod.price
                    : prod.price;
                const billableQuantity = resolveBillableQuantity(prod);
                const extra = Number(prod.extraAmount) || 0;
                const baseCents = prod.manualUnitPrice != null
                    ? Math.round(resolveOrderLineCharge(Object.assign(Object.assign({}, prod), { discountAmount: 0 }), isExpress).subtotalBeforeDiscount * 100)
                    : Math.round(((Number(prodPrice) || 0) * billableQuantity + extra) * 100);
                if (baseCents > 0) {
                    eligibleEntries.push({ idx, baseCents });
                }
            });
            if (eligibleEntries.length > 0) {
                const couponCents = Math.max(0, Math.round(couponValue * 100));
                const sliceCents = allocateFixedDiscountAcrossLines(couponCents, eligibleEntries.map((e) => e.baseCents));
                const newProdsCopy = newOrderProds.slice();
                eligibleEntries.forEach((e, i) => {
                    const slicePesos = +(sliceCents[i] / 100).toFixed(2);
                    newProdsCopy[e.idx] = Object.assign(Object.assign({}, newProdsCopy[e.idx]), { discountAmount: slicePesos, discountAmountMode: 'line_total' });
                });
                newOrderProds = newProdsCopy;
            }
        }
        else {
            // applyOnceOnOrder=false: per-unit fixed discount. Total per eligible
            // line is value * qty (was a flat `value` previously — silent bug).
            newOrderProds = newOrderProds.map((prod) => {
                if (!isEligibleProd(prod))
                    return prod;
                const billableQuantity = resolveBillableQuantity(prod);
                const lineDiscount = +(couponValue * billableQuantity).toFixed(2);
                return Object.assign(Object.assign({}, prod), { discountAmount: lineDiscount, discountAmountMode: 'line_total' });
            });
        }
    }
    if (discountCode.buyAndGetConditions && discountCode.type === DiscountCodeTypes.BUY_X_GET_Y) {
        const conditionSet = Array.isArray(discountCode.buyAndGetConditions)
            ? discountCode.buyAndGetConditions[0]
            : discountCode.buyAndGetConditions;
        if (conditionSet) {
            const buyConditions = Array.isArray(conditionSet === null || conditionSet === void 0 ? void 0 : conditionSet.buyConditions)
                ? conditionSet.buyConditions
                : [];
            const getConditions = Array.isArray(conditionSet === null || conditionSet === void 0 ? void 0 : conditionSet.getConditions)
                ? conditionSet.getConditions
                : [];
            const buyQualificationProducts = productsArr.filter((product) => !isNoChargeReturnLine(returnPricing, product));
            const fulfillmentCount = getFulfillmentCount(buyConditions, buyQualificationProducts);
            if (fulfillmentCount) {
                for (const getCondition of getConditions) {
                    const resolvedReward = resolveGetConditionProductSnapshot(getCondition, isExpress);
                    if (!resolvedReward)
                        continue;
                    const percentageDiscount = Number((_b = conditionSet.discountValue) !== null && _b !== void 0 ? _b : 0) / 100;
                    const discountAmount = conditionSet.getDiscountType === BuyAndGetConditionsTypes.FREE
                        ? resolvedReward.price
                        : +(percentageDiscount * resolvedReward.price).toFixed(2);
                    const rewardQuantity = fulfillmentCount * Number((_c = getCondition === null || getCondition === void 0 ? void 0 : getCondition.qty) !== null && _c !== void 0 ? _c : 0);
                    buyAndGetProds.push(Object.assign(Object.assign({}, resolvedReward.productSnapshot), { qty: rewardQuantity, quantity: rewardQuantity, discountAmount, discountAmountMode: 'per_unit', isBuyAndGetProduct: true }));
                }
            }
        }
    }
    else if (discountCode.type === DiscountCodeTypes.FREE_ITEM) {
        const freeProductsSettinggs = discountCode.freeProductSetting[0];
        const freeProduct = freeProductsSettinggs.product;
        const prodPrice = isExpress ? freeProduct.expressPrice : freeProduct.price;
        // Para free item, se asume que el producto es gratuito, es decir, se descuenta el precio del producto
        const discountAmount = prodPrice;
        buyAndGetProds.push(Object.assign(Object.assign({}, freeProduct), { qty: freeProductsSettinggs.qty, quantity: freeProductsSettinggs.qty, discountAmount: discountAmount, discountAmountMode: 'per_unit', isBuyAndGetProduct: true, isFreeItem: true }));
    }
    return { newOrderProds, buyAndGetProds };
};
